/**
 * Public landing-trial course, readable during SSR / prerender.
 *
 * The landing hero and header CTA advertise the admin-configured trial price.
 * That value used to be fetched only in the browser, so the prerendered HTML
 * (what crawlers and the first paint see) carried the hardcoded default of 99
 * and then swapped after hydration. Courses expose the trial columns to anon
 * reads, so the price can be read on the server with the publishable key and
 * shipped inside the first byte of HTML.
 */
import { createServerFn } from "@tanstack/react-start";
import { memoPublic } from "@/lib/serverMemo";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export interface PublicTrialCourse {
  id: string;
  title: string;
  trial_price: number | null;
  trial_currency: string;
  trial_days: number;
  access_mode: string;
  is_landing_trial: boolean;
}

const SELECT = "id, title, trial_price, trial_currency, trial_days, access_mode, is_landing_trial";

export const getPublicLandingTrialCourse = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicTrialCourse | null> =>
    (await memoPublic("landing_trial", 60_000, 2_500, async (): Promise<PublicTrialCourse | null> => {
    // The prerender pass runs the built worker from a plain Node script, where
    // the Worker-injected process env is absent — fall back to the build-time
    // inlined VITE_* values so static HTML still carries the live price.
    const url = process.env["SUPABASE_URL"] || import.meta.env["VITE_SUPABASE_URL"];
    const key =
      process.env["SUPABASE_PUBLISHABLE_KEY"] || import.meta.env["VITE_SUPABASE_PUBLISHABLE_KEY"];
    if (!url || !key) return null;

    const supabasePublic = createClient<Database>(url, key, {
      auth: { persistSession: false },
      global: {
        // sb_ publishable keys are opaque, not JWTs — send them as `apikey` only.
        fetch: (input, init) => {
          const headers = new Headers(init?.headers);
          if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
            headers.delete("Authorization");
          }
          headers.set("apikey", key);
          return fetch(input, { ...init, headers });
        },
      },
    });

    const { data, error } = await supabasePublic
      .from("courses")
      .select(SELECT)
      .eq("is_landing_trial", true)
      .is("deleted_at", null)
      .maybeSingle();

    if (error) throw new Error("trial unavailable");
    if (!data) return null;
    return data as PublicTrialCourse;
  })) ?? null,
);
